/**
 * DevicePagePremium.jsx
 *
 * Versão Premium do formulário de Device usando os novos componentes.
 * Mantém compatibilidade com EditItemView (que gerencia o loading).
 */

import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TuneIcon from '@mui/icons-material/Tune';
import ImageIcon from '@mui/icons-material/Image';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { MuiFileInput } from 'mui-file-input';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useAdministrator, useManager } from '../../common/util/permissions';
import { useCatch } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import deviceCategories from '../../common/util/deviceCategories';

// Components
import PremiumTabs, { PremiumTabPanel } from '../../common/components/PremiumTabs';
import EditItemView from '../components/EditItemView';
import EditAttributesAccordion from '../components/EditAttributesAccordion';
import SelectField from '../../common/components/SelectField';
import SettingsMenu from '../components/SettingsMenu';
import QrCodeDialog from '../../common/components/QrCodeDialog';
import useDeviceAttributes from '../../common/attributes/useDeviceAttributes';
import useCommonDeviceAttributes from '../../common/attributes/useCommonDeviceAttributes';

// Helpers
import { ensureFormStructure, setNestedValue, getNestedValue } from '../../common/hooks/useCrudForm';

/**
 * Mapeia nomes de ícones para componentes
 */
const iconMap = {
  InfoIcon: <InfoIcon />,
  DirectionsCarIcon: <DirectionsCarIcon />,
  TuneIcon: <TuneIcon />,
  ImageIcon: <ImageIcon />,
};

const DevicePagePremium = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Permissions
  const isAdmin = useAdministrator();
  const isManager = useManager();
  const canEdit = isAdmin || isManager;

  // Attributes definitions
  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const deviceAttributes = useDeviceAttributes(t);

  // URL params
  const [searchParams] = useSearchParams();
  const uniqueIdFromUrl = searchParams.get('uniqueId');

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // QR Code dialog
  const [showQr, setShowQr] = useState(false);

  // Image upload
  const [imageFile, setImageFile] = useState(null);

  // Item state (managed by EditItemView)
  const [item, setItemRaw] = useState(null);

  // Wrapper to ensure form structure
  const setItem = (newItem) => {
    setItemRaw(ensureFormStructure(newItem));
  };

  // Update field helper
  const updateField = (path, value) => {
    setItem(setNestedValue({ ...item }, path, value));
  };

  // Get nested value helper
  const getValue = (path) => getNestedValue(item, path);

  // Handle image upload
  const handleFileInput = useCatch(async (newFile) => {
    setImageFile(newFile);
    if (newFile && item?.id) {
      const response = await fetchOrThrow(`/api/devices/${item.id}/image`, {
        method: 'POST',
        body: newFile,
      });
      updateField('attributes.deviceImage', await response.text());
    } else if (!newFile && item?.attributes?.deviceImage) {
      const newAttributes = { ...item.attributes };
      delete newAttributes.deviceImage;
      setItem({ ...item, attributes: newAttributes });
    }
  });

  // Filter attributes (keep form fields separate)
  const filterAttributes = (attributes) => {
    if (!attributes) return {};

    const formFields = attributes.form || {};
    const filteredAttributes = { form: { ...formFields } };

    Object.keys(attributes).forEach((key) => {
      if (key === 'form') return;
      const value = attributes[key];
      if (value !== null && value !== undefined && typeof value !== 'object') {
        filteredAttributes[key] = value;
      }
    });

    return filteredAttributes;
  };

  // Update attributes from EditAttributesAccordion
  const updateAttributes = (newAttributes) => {
    const updatedAttributes = { ...newAttributes };
    if (item.attributes?.form) {
      updatedAttributes.form = item.attributes.form;
    }
    setItem({ ...item, attributes: updatedAttributes });
  };

  // Validation
  const validate = () => item && item.name && item.uniqueId;

  // Build tabs array
  const baseTabs = [
    { label: t('sharedRequired'), icon: iconMap.InfoIcon },
    { label: t('sharedExtra'), icon: iconMap.DirectionsCarIcon },
    { label: t('sharedAttributes'), icon: iconMap.TuneIcon },
  ];

  // Add image tab only if editing existing device
  const tabs = item?.id
    ? [...baseTabs, { label: t('attributeDeviceImage'), icon: iconMap.ImageIcon }]
    : baseTabs;

  // Device categories for select
  const categoryOptions = deviceCategories
    .map((category) => ({
      id: category,
      name: t(`category${category.replace(/^\w/, (c) => c.toUpperCase())}`),
    }))
    .filter((a) => a.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Common input styles
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: tokens?.surface?.elevated || 'transparent',
      '& fieldset': {
        borderColor: tokens?.border?.default || 'rgba(255,255,255,0.1)',
      },
      '&:hover fieldset': {
        borderColor: tokens?.accent?.primary || '#00d4aa',
      },
    },
  };

  return (
    <EditItemView
      endpoint="devices"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDevice']}
    >
      {item && (
        <Paper
          sx={{
            backgroundColor: tokens?.surface?.glass || 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
            overflow: 'hidden',
          }}
        >
          {/* Premium Tabs */}
          <PremiumTabs
            tabs={tabs}
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          />

          {/* Tab Content Container */}
          <Box sx={{ px: 3, pb: 3 }}>
            {/* Tab 0: Geral */}
            <PremiumTabPanel value={activeTab} index={0}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Seção: Identificação */}
                <Typography
                  variant="overline"
                  sx={{
                    color: tokens?.text?.muted || '#6e7681',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('sharedIdentification') || 'Identificação'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <SelectField
                      fullWidth
                      value={item.groupId}
                      onChange={(e) => updateField('groupId', Number(e.target.value))}
                      endpoint="/api/groups"
                      label={t('groupParent')}
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedName')}
                      value={item.name || ''}
                      onChange={(e) => updateField('name', e.target.value)}
                      required
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('deviceIdentifier')}
                      value={item.uniqueId || ''}
                      onChange={(e) => updateField('uniqueId', e.target.value)}
                      required
                      disabled={!canEdit || Boolean(uniqueIdFromUrl)}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Seção: Detalhes */}
                <Typography
                  variant="overline"
                  sx={{
                    color: tokens?.text?.muted || '#6e7681',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    mt: 1,
                  }}
                >
                  {t('sharedDetails') || 'Detalhes'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedDevicePlate')}
                      value={getValue('attributes.form.plate') || ''}
                      onChange={(e) => updateField('attributes.form.plate', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('deviceContact')}
                      value={item.contact || ''}
                      onChange={(e) => updateField('contact', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <SelectField
                      fullWidth
                      value={item.category || 'default'}
                      onChange={(e) => updateField('category', e.target.value)}
                      data={categoryOptions}
                      label={t('deviceCategory')}
                      disabled={!canEdit}
                    />
                  </Box>
                </Box>

                {/* Seção: Configurações */}
                <Typography
                  variant="overline"
                  sx={{
                    color: tokens?.text?.muted || '#6e7681',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    mt: 1,
                  }}
                >
                  {t('sharedSettings') || 'Configurações'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <SelectField
                      fullWidth
                      value={item.calendarId}
                      onChange={(e) => updateField('calendarId', Number(e.target.value))}
                      endpoint="/api/calendars"
                      label={t('sharedCalendar')}
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label={t('userExpirationTime')}
                      value={item.expirationTime ? new Date(item.expirationTime).toISOString().slice(0, 10) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          updateField('expirationTime', new Date(e.target.value).toISOString());
                        } else {
                          updateField('expirationTime', null);
                        }
                      }}
                      disabled={!canEdit}
                      InputLabelProps={{ shrink: true }}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Status e Ações */}
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.disabled || false}
                        onChange={(e) => updateField('disabled', e.target.checked)}
                        sx={{
                          color: tokens?.text?.secondary || '#8b949e',
                          '&.Mui-checked': {
                            color: tokens?.accent?.primary || '#00d4aa',
                          },
                        }}
                      />
                    }
                    label={t('sharedDisabled')}
                    disabled={!canEdit}
                    sx={{ color: tokens?.text?.primary || '#f0f6fc' }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<QrCodeIcon />}
                    onClick={() => setShowQr(true)}
                    disabled={!canEdit}
                    sx={{
                      borderColor: tokens?.accent?.primary || '#00d4aa',
                      color: tokens?.accent?.primary || '#00d4aa',
                      borderRadius: tokens?.radius?.md || '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: tokens?.accent?.subtle || 'rgba(0,212,170,0.1)',
                        borderColor: tokens?.accent?.primary || '#00d4aa',
                      },
                    }}
                  >
                    {t('sharedQrCode')}
                  </Button>
                </Box>
              </Box>
            </PremiumTabPanel>

            {/* Tab 1: Veículo */}
            <PremiumTabPanel value={activeTab} index={1}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Informações do Veículo */}
                <Typography
                  variant="overline"
                  sx={{ color: tokens?.text?.muted || '#6e7681', fontWeight: 600, letterSpacing: '0.05em' }}
                >
                  {t('sharedVehicleInfo') || 'Informações do Veículo'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('deviceBrand')}
                      value={getValue('attributes.form.brand') || ''}
                      onChange={(e) => updateField('attributes.form.brand', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('deviceModel')}
                      value={getValue('attributes.form.model') || ''}
                      onChange={(e) => updateField('attributes.form.model', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('deviceColor')}
                      value={getValue('attributes.form.color') || ''}
                      onChange={(e) => updateField('attributes.form.color', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Informações Técnicas */}
                <Typography
                  variant="overline"
                  sx={{ color: tokens?.text?.muted || '#6e7681', fontWeight: 600, letterSpacing: '0.05em', mt: 1 }}
                >
                  {t('sharedTechnicalInfo') || 'Informações Técnicas'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('deviceFuelconsumption')}
                      value={getValue('attributes.form.fuelConsumption') || ''}
                      onChange={(e) => updateField('attributes.form.fuelConsumption', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedPhone')}
                      value={item.phone || ''}
                      onChange={(e) => updateField('phone', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedEquipmentModel')}
                      value={item.model || ''}
                      onChange={(e) => updateField('model', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Informações do Chip */}
                <Typography
                  variant="overline"
                  sx={{ color: tokens?.text?.muted || '#6e7681', fontWeight: 600, letterSpacing: '0.05em', mt: 1 }}
                >
                  {t('sharedChipInfo') || 'Informações do Chip'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedOperatorChip')}
                      value={getValue('attributes.form.operator') || ''}
                      onChange={(e) => updateField('attributes.form.operator', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedVendorChip')}
                      value={getValue('attributes.form.vendor') || ''}
                      onChange={(e) => updateField('attributes.form.vendor', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedIMEIChip')}
                      value={getValue('attributes.form.iccid') || ''}
                      onChange={(e) => updateField('attributes.form.iccid', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                </Box>

                {/* Documentação */}
                <Typography
                  variant="overline"
                  sx={{ color: tokens?.text?.muted || '#6e7681', fontWeight: 600, letterSpacing: '0.05em', mt: 1 }}
                >
                  {t('sharedDocumentation') || 'Documentação'}
                </Typography>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedPrefixo')}
                      value={getValue('attributes.form.prefix') || ''}
                      onChange={(e) => updateField('attributes.form.prefix', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedChassi')}
                      value={getValue('attributes.form.chassi') || ''}
                      onChange={(e) => updateField('attributes.form.chassi', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('sharedRenavam')}
                      value={getValue('attributes.form.vin') || ''}
                      onChange={(e) => updateField('attributes.form.vin', e.target.value)}
                      disabled={!canEdit}
                      sx={inputSx}
                    />
                  </Box>
                </Box>
              </Box>
            </PremiumTabPanel>

            {/* Tab 2: Atributos */}
            <PremiumTabPanel value={activeTab} index={2}>
              <EditAttributesAccordion
                attributes={filterAttributes(item.attributes)}
                setAttributes={updateAttributes}
                definitions={{ ...commonDeviceAttributes, ...deviceAttributes }}
                disabled={!canEdit}
              />
            </PremiumTabPanel>

            {/* Tab 3: Imagem */}
            {item.id && (
              <PremiumTabPanel value={activeTab} index={3}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    py: 2,
                  }}
                >
                  {item.attributes?.deviceImage && (
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 400,
                        borderRadius: tokens?.radius?.lg || '12px',
                        overflow: 'hidden',
                        border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      }}
                    >
                      <img
                        src={`/api/media/${item.attributes.deviceImage}`}
                        alt={item.name}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                      />
                    </Box>
                  )}
                  <Box sx={{ width: '100%', maxWidth: 400 }}>
                    <MuiFileInput
                      fullWidth
                      placeholder={t('attributeDeviceImage')}
                      value={imageFile}
                      onChange={handleFileInput}
                      inputProps={{ accept: 'image/*' }}
                      disabled={!canEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: tokens?.surface?.elevated || 'transparent',
                          borderRadius: tokens?.radius?.md || '8px',
                          '& fieldset': {
                            borderColor: tokens?.border?.default || 'rgba(255,255,255,0.1)',
                          },
                          '&:hover fieldset': {
                            borderColor: tokens?.accent?.primary || '#00d4aa',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </PremiumTabPanel>
            )}
          </Box>
        </Paper>
      )}
      <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />
    </EditItemView>
  );
};

export default DevicePagePremium;
